import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

//* Importando os componentes List e Loading
import { List } from "../../../component/list/list";
import { Loading } from "../../../component/common/Loading";

//* Modais de listas de questões (favoritos)
import SaveToListModal from "../../../component/QuestionLists/SaveToListModal";
import MyListsModal from "../../../component/QuestionLists/MyListsModal";

//* Constants
import { UserRole } from "../../../constants/enum/UserRole";

//* Importando o hook useQuestions
import { useQuestions } from "../../../hooks/questions/useQuestions";
import { questionAPI } from "../../../hooks/questions/questionsAPI";

//* Importando o hook useDisciplines e o componente Dropdown do Bootstrap para o filtro
import { useDisciplines } from "../../../hooks/disciplines/useDisciplines";
import { Dropdown } from "react-bootstrap";

//* Importando o componente ModalManage
import ModalManage from "./components/ModalManage";

//* Importando funções de mapeamento
import {
	mapEducationLevel,
	mapDifficultyLevel,
} from "../../../utils/questions/questionsMap";

const debounce = require("lodash.debounce");

// mode "own": banco da própria organização (com filtro por autor + cadastro).
// mode "public": questões públicas/banca (organization_id IS NULL), somente leitura.
export function QuestionsView({ mode = "own" }: { mode?: "own" | "public" } = {}) {
	const isPublic = mode === "public";
	const token = localStorage.getItem("token") || "";
	const navigate = useNavigate();

	// Estados para deleção, pesquisa e total de questões
	const [showWarning, setShowWarning] = useState(false);
	const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);
	const [searchInputValue, setSearchInputValue] = useState<string>("");
	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [totalQuestions, setTotalQuestions] = useState<number>(0);

	// Estados para ModalManage
	const [showModalManage, setShowModalManage] = useState<boolean>(false);
	const [modalQuestionId, setModalQuestionId] = useState<string | null>(null);

	// Estados para as listas de questões (favoritos)
	const role = useSelector((state: any) => state.auth.me?.role?.slug);
	const canUseLists =
		role === UserRole.ADMIN_USER || role === UserRole.TEACHER_USER;
	const [showSaveToList, setShowSaveToList] = useState<boolean>(false);
	const [saveToListQuestionId, setSaveToListQuestionId] = useState<string | null>(
		null
	);
	const [showMyLists, setShowMyLists] = useState<boolean>(false);

	const handleSaveToListClick = useCallback((id: string) => {
		setSaveToListQuestionId(id);
		setShowSaveToList(true);
	}, []);

	const {
		questions,
		loading,
		error,
		setSearch,
		pagination,
		setPagination,
		changePage,
		isDeleting,
		deleteQuestion, // função de deleção via hook
		filters,
		updateFilters,
	} = useQuestions(token, { ownOnly: !isPublic, bancaOnly: isPublic });

	// Hook para carregar as disciplinas para o filtro
	const { discipline } = useDisciplines(token);

	// Autores das questões da organização, para o filtro "Criado por" (só no modo próprio)
	const { data: authors = [] } = useQuery({
		queryKey: ["question-authors"],
		queryFn: () => questionAPI.getAuthors(token),
		enabled: !!token && !isPublic,
		staleTime: 5 * 60 * 1000,
	});

	// Atualiza o total de questões com base na paginação
	useEffect(() => {
		setTotalQuestions(pagination.totalItems || 0);
	}, [pagination.totalItems]);

	// Pesquisa com debounce para otimizar as chamadas
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearch = useCallback(
		debounce((value: string) => {
			if (value.trim().length >= 3) {
				setSearch(value);
			} else {
				setSearch("");
				setPagination((prev) => ({ ...prev, currentPage: 1 }));
			}
			setIsSearching(false);
		}, 500),
		[setSearch, setPagination]
	);

	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	const handleSearchInputChange = useCallback(
		(value: string) => {
			setSearchInputValue(value);
			setIsSearching(true);
			debouncedSearch(value);
		},
		[debouncedSearch]
	);

	// Confirma a deleção; se a página atual tiver apenas 1 item, volta para a página anterior
	const handleDeleteConfirm = useCallback(async () => {
		if (questionToDelete) {
			try {
				const currentPageItems = questions.length;
				const currentPage = pagination.currentPage;

				await deleteQuestion(questionToDelete);

				if (currentPageItems === 1 && currentPage > 1) {
					changePage(currentPage - 1);
				}

				setShowWarning(false);
				setQuestionToDelete(null);
			} catch (error) {
				console.error("Erro ao deletar questão:", error);
			}
		}
	}, [
		questionToDelete,
		deleteQuestion,
		questions.length,
		pagination.currentPage,
		changePage,
	]);

	const handleDeleteActionClick = useCallback((id: string) => {
		setQuestionToDelete(id);
		setShowWarning(true);
	}, []);

	const handleChangePagination = useCallback(
		(value: number) => {
			changePage(value);
		},
		[changePage]
	);

	// Filtro por disciplina
	const handleDisciplineChange = useCallback(
		(newDisciplineId: string | undefined) => {
			updateFilters({ disciplineId: newDisciplineId });
		},
		[updateFilters]
	);

	const handleYearChange = useCallback(
		(newYear: number | undefined) => {
			updateFilters({ year: newYear });
		},
		[updateFilters]
	);

	const handlePhaseChange = useCallback(
		(newPhase: string | undefined) => {
			updateFilters({ phase: newPhase });
		},
		[updateFilters]
	);

	const handleCreatedByChange = useCallback(
		(newCreatedBy: string | undefined) => {
			updateFilters({ createdBy: newCreatedBy });
		},
		[updateFilters]
	);

	const yearOptions = Array.from({ length: 14 }, (_, idx) => 2026 - idx);

	const filterComponents = (
		<div className="d-flex align-items-center flex-wrap">
			<Dropdown className="d-flex align-items-center ms-2">
				<Dropdown.Toggle variant="secondary" id="dropdown-year">
					{filters.year ? filters.year : "Todos os anos"}
				</Dropdown.Toggle>
				<Dropdown.Menu style={{ maxHeight: "220px", overflowY: "auto" }}>
					<Dropdown.Item onClick={() => handleYearChange(undefined)}>
						Todos os anos
					</Dropdown.Item>
					{yearOptions.map((year) => (
						<Dropdown.Item key={year} onClick={() => handleYearChange(year)}>
							{year}
						</Dropdown.Item>
					))}
				</Dropdown.Menu>
			</Dropdown>

			<Dropdown className="d-flex align-items-center ms-2">
				<Dropdown.Toggle variant="info" id="dropdown-phase">
					{filters.phase || "Todas as fases"}
				</Dropdown.Toggle>
				<Dropdown.Menu>
					<Dropdown.Item onClick={() => handlePhaseChange(undefined)}>
						Todas as fases
					</Dropdown.Item>
					<Dropdown.Item onClick={() => handlePhaseChange("1a fase")}>
						1a fase
					</Dropdown.Item>
					<Dropdown.Item onClick={() => handlePhaseChange("2a fase")}>
						2a fase
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>

			<Dropdown className="d-flex align-items-center ms-2">
				<Dropdown.Toggle variant="success" id="dropdown-discipline">
					{filters.disciplineId
						? discipline.find((d: any) => d.id === filters.disciplineId)?.name
						: "Todas as Disciplinas"}
				</Dropdown.Toggle>
				<Dropdown.Menu style={{ maxHeight: "200px", overflowY: "auto" }}>
					<Dropdown.Item onClick={() => handleDisciplineChange(undefined)}>
						Todas as Disciplinas
					</Dropdown.Item>
					{discipline.map((d: any) => (
						<Dropdown.Item
							key={d.id}
							onClick={() => handleDisciplineChange(d.id)}
						>
							{d.name}
						</Dropdown.Item>
					))}
				</Dropdown.Menu>
			</Dropdown>

			{!isPublic && (
				<Dropdown className="d-flex align-items-center ms-2">
					<Dropdown.Toggle variant="dark" id="dropdown-created-by">
						{filters.createdBy
							? authors.find((a) => a.id === filters.createdBy)?.name ??
							  "Criado por"
							: "Todos os autores"}
					</Dropdown.Toggle>
					<Dropdown.Menu style={{ maxHeight: "220px", overflowY: "auto" }}>
						<Dropdown.Item onClick={() => handleCreatedByChange(undefined)}>
							Todos os autores
						</Dropdown.Item>
						{authors.map((a) => (
							<Dropdown.Item
								key={a.id}
								onClick={() => handleCreatedByChange(a.id)}
							>
								{a.name}
							</Dropdown.Item>
						))}
					</Dropdown.Menu>
				</Dropdown>
			)}
		</div>
	);

	// Função para abrir o ModalManage
	const handleManagerActionClick = useCallback((id: string) => {
		setModalQuestionId(id);
		setShowModalManage(true);
	}, []);

	// Função para fechar o ModalManage
	const handleCloseModalManage = useCallback(() => {
		setShowModalManage(false);
		setModalQuestionId(null);
	}, []);

	const transformedItems = useMemo(
		() =>
			questions.map((question) => ({
				...question,
				key: question.id,
				displayLabel: question.displayLabel || "-",
				year: question.year || "-",
				phase: question.phase || "-",
				educationLevel: mapEducationLevel(question.educationLevel),
				difficultyLevel: mapDifficultyLevel(question.difficultyLevel),
				disciplineName: question.discipline?.name || "-",
				createdBy: question.createdByName || "-",
			})),
		[questions]
	);

	if (isDeleting) {
		return <Loading />;
	}

	if (error) {
		return <div>Erro: {error}</div>;
	}


	return (
		<div className="row">
			<List
				search_input_value={searchInputValue}
				search_text="Pesquise pelo enunciado da questão"
				onChangeSearchInput={handleSearchInputChange}
				onSearchButtonClick={() => {
					/* busca controlada por debounce no input */
				}}
				title={`${isPublic ? "Questões Públicas" : "Questões"} (Total: ${totalQuestions})`}
				not_found_message="Nenhuma questão encontrada"
				additionalButtons={[
					{
						label: "✨ Criar com IA",
						eventHandler: () => navigate("/questions/create/ai"),
					},canUseLists
						? [
								{
									label: "Minhas listas",
									eventHandler: () => setShowMyLists(true),
								},
						  ]
						: undefined
				]}
				button_link={isPublic ? undefined : "/questions/create"}
				button_text={isPublic ? undefined : "Adicionar questão"}
				deleting_message="Ao deletar esta questão, ela não estará mais disponível no sistema"
				onDeleteCancel={() => setShowWarning(false)}
				onDeleteConfirm={handleDeleteConfirm}
				onDeleteActionClick={handleDeleteActionClick}
				onEditActionClick={(id) => navigate(`/questions/edit/${id}`)}
				onManagerActionClick={handleManagerActionClick}
				showSaveToList={canUseLists}
				onSaveToListActionClick={handleSaveToListClick}
				showLifecycle
				onLifecycleActionClick={(id) => navigate(`/questions/lifecycle/${id}`)}
				onChangePagination={handleChangePagination}
				show_warning={showWarning}
				loading_screen={loading || isSearching}
				items={transformedItems}
				pagination={{
					last_page: pagination.totalPages,
					current_page: pagination.currentPage,
				}}
				fields={[
					{ name: "displayLabel", type: "text", header: "Identificação" },
					{ name: "year", type: "text", header: "Ano" },
					{ name: "phase", type: "text", header: "Fase" },
					{ name: "educationLevel", type: "text", header: "Nível de Educação" },
					{
						name: "difficultyLevel",
						type: "text",
						header: "Nível de Dificuldade",
					},
					{ name: "disciplineName", type: "text", header: "Disciplina" },
					{ name: "createdBy", type: "text", header: "Criado por" },
					{ name: "createdAt", type: "date", header: "Data de Criação" },
				]}
				loading_search={isSearching}
				additionalFilters={filterComponents}
			/>

			{/* Modal de gerenciamento */}
			<ModalManage
				id={modalQuestionId}
				token={token}
				showModalManage={showModalManage}
				setShowModalManage={handleCloseModalManage}
			/>

			{/* Modais das listas de questões (favoritos) */}
			{canUseLists && (
				<>
					<SaveToListModal
						show={showSaveToList}
						onHide={() => setShowSaveToList(false)}
						questionId={saveToListQuestionId}
						token={token}
					/>
					<MyListsModal
						show={showMyLists}
						onHide={() => setShowMyLists(false)}
						token={token}
					/>
				</>
			)}
		</div>
	);
}
